import type { RankingEntry } from '@db/types/UserGameStats';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { dummyRankingData } from '@/consts/dummyRankingData';
import { useScoreSave } from '@/hooks/useScoreSave';
import { useAuthStore } from '@/store/authStore';
import { useGameStore } from '@/store/gameStore';
import { fetchRanking } from '@/utils/fetchRanking';

const getRankBadge = (rank: number) => {
  switch (rank) {
    case 1:
      return <span>🥇</span>;
    case 2:
      return <span>🥈</span>;
    case 3:
      return <span>🥉</span>;
    default:
      return <span>{rank}</span>;
  }
};

function ResultsPage() {
  const lastScore = useGameStore((state) => state.lastScore);
  const { saveState, hasPendingSave, pendingScore } = useScoreSave();
  const [rankingData, setRankingData] =
    useState<RankingEntry[]>(dummyRankingData);

  useEffect(() => {
    fetchRanking().then((data) => {
      setRankingData(data ?? dummyRankingData);
    });
  }, []);

  useEffect(() => {
    if (saveState !== 'saved') return;
    fetchRanking().then((data) => {
      setRankingData(data ?? dummyRankingData);
    });
  }, [saveState]);

  const handleLogin = () => {
    useAuthStore
      .getState()
      .signInWithGoogle(window.location.origin + '/results')
      .catch((e) => window.alert(`ログインに失敗しました: ${e}`));
  };

  return (
    <div className="h-svh max-w-full overflow-clip bg-linear-to-br from-indigo-600 via-purple-600 to-pink-600 grid place-items-center">
      <div className="container px-8 md:px-0 grid gap-24 place-items-center grid-auto-rows-max">
        <h1 className="text-2xl md:text-4xl font-bold text-white text-center">
          ランキング
        </h1>

        {lastScore > 0 && (
          <p className="bg-white rounded-lg py-[0.8em] px-[1.4em] text-center text-base text-purple-700">
            <b>前回のスコア: {lastScore.toLocaleString()}</b>
          </p>
        )}

        {saveState === 'saved' && pendingScore !== null && (
          <div className="grid place-items-center bg-white gap-12 border border-green-200 rounded-lg p-[1em]">
            <p className="text-center text-green-800 font-bold text-lg">
              スコアを保存しました ({pendingScore.toLocaleString()}点)
            </p>
          </div>
        )}

        {hasPendingSave && saveState !== 'saved' && (
          <div className="grid place-items-center bg-white gap-12 border border-amber-200 rounded-lg p-[1em]">
            <div className="grid gap-8 place-items-center">
              <p className="text-center text-red-800 font-bold text-lg">
                {saveState === 'saving'
                  ? 'スコアを保存中...'
                  : 'セッションが切れました'}
              </p>
              {saveState !== 'saving' && (
                <p className="text-center text-gray-700 text-sm">
                  再度ログインするとスコアが保存されます
                </p>
              )}
            </div>
            {saveState !== 'saving' && (
              <Button onClick={handleLogin}>ログインして保存</Button>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg px-4 w-full md:max-w-lg overflow-hidden">
          <table className="grid w-full">
            <thead>
              <tr className="grid grid-cols-[80px_minmax(160px,1fr)_100px] border-b-2 border-purple-200 text-small md:text-base">
                <th className="text-center px-[1em] py-[0.8em] pb-[0.4em] text-purple-900 font-bold">
                  順位
                </th>
                <th className="text-left px-[1em] py-[0.8em] pb-[0.4em] text-purple-900 font-bold">
                  プレイヤー
                </th>
                <th className="text-center px-[1em] py-[0.8em] pb-[0.4em] text-purple-900 font-bold">
                  スコア
                </th>
              </tr>
            </thead>
            <tbody className="grid max-h-[50svh] h-full overflow-y-auto">
              {rankingData.map((entry) => (
                <tr
                  key={`${entry.rank}-${entry.user_name}-${entry.high_score}`}
                  className="grid grid-cols-[80px_minmax(160px,1fr)_80px] md:grid-cols-[80px_minmax(200px,1fr)_100px] items-center border-b border-purple-100 hover:bg-purple-50 transition-colors text-lg"
                >
                  <td className="px-[1em] py-[0.8em] pb-[0.6em] text-center text-lg">
                    {getRankBadge(entry.rank ?? 0)}
                  </td>
                  <td
                    className="px-[1em] py-[0.8em] pb-[0.6em] text-purple-700 data-[ranker='true']:font-bold data-[rank='1']:text-yellow-700 data-[rank='2']:text-gray-700 data-[rank='3']:text-orange-700"
                    data-ranker={
                      entry.rank && entry.rank <= 3 ? 'true' : 'false'
                    }
                    data-rank={entry.rank}
                  >
                    {entry.user_name}
                  </td>
                  <td
                    className="px-[1em] py-[0.8em] pb-[0.6em] text-right text-purple-700 data-[ranker='true']:font-bold data-[rank='1']:text-yellow-700 data-[rank='2']:text-gray-700 data-[rank='3']:text-orange-700"
                    data-ranker={
                      entry.rank && entry.rank <= 3 ? 'true' : 'false'
                    }
                    data-rank={entry.rank}
                  >
                    {entry.high_score.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <Button asChild>
            <Link to="/">トップに戻る</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;
